import React, { useState, useMemo, useCallback } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

const ViewKycDetails = ({
  baseurl,
  token,
  selectedDriver,
  setSelectedDriver,
  setKycDrivers,
  activeTab,
    updateDriverKycStatus,
  fetchDriverKycDetails,
  handleStepSubmit,
  previewImage,
  setPreviewImage,
  openKycDialogId,
  setOpenKycDialogId,
  AdminId,
  configRows = [],
  chargeRows = [],
}) => {
  const [driverKycStatus, setLocalDriverKycStatus] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectStep, setRejectStep] = useState("");
  const [rejectComment, setRejectComment] = useState("");

  const kycId = useMemo(() => {
    if (!selectedDriver) return null;
    const id = selectedDriver?.id || selectedDriver?.driverId;
    return id;
  }, [selectedDriver?.id, selectedDriver?.driverId]);

  const images = useMemo(() => {
    if (!selectedDriver) return [];

    const driverIdentifier = selectedDriver.id;

    return [
      {
        src:
          selectedDriver.profile_photo ||
          selectedDriver.profilePicture ||
          `${baseurl}/uploads/kyc/${driverIdentifier}_profile_photo.jpg`,
        label: "Profile Photo",
        field: "profile_photo",
      },
      {
        src:
          selectedDriver.aadhaar_front ||
          selectedDriver.aadharFrontPhoto ||
          `${baseurl}/uploads/kyc/${driverIdentifier}_aadhaar_front.jpg`,
        label: "Aadhaar Front",
        field: "aadhaar_front",
      },
      {
        src:
          selectedDriver.aadhaar_back ||
          selectedDriver.aadharBackPhoto ||
          `${baseurl}/uploads/kyc/${driverIdentifier}_aadhaar_back.jpg`,
        label: "Aadhaar Back",
        field: "aadhaar_back",
      },
      {
        src:
          selectedDriver.pan_front ||
          selectedDriver.panCardFrontPhoto ||
          `${baseurl}/uploads/kyc/${driverIdentifier}_pan_front.jpg`,
        label: "PAN Card Front",
        field: "pan_front",
      },
      {
        src:
          selectedDriver.driving_licence_img ||
          selectedDriver.drivingLicenseFrontPhoto ||
          `${baseurl}/uploads/kyc/${driverIdentifier}_dl_front.jpg`,
        label: "DL Image",
        field: "driving_licence_img",
      },
      {
        src:
          selectedDriver.vehicle_name_plate_img ||
          selectedDriver.licensePlatePhoto ||
          `${baseurl}/uploads/kyc/${driverIdentifier}_vehicle_plate.jpg`,
        label: "Number Plate",
        field: "vehicle_name_plate_img",
      },
    ];
  }, [selectedDriver, baseurl]);

  const statusInfo = useMemo(() => {
    const currentStatus =
      driverKycStatus?.status || selectedDriver?.status || "";

    return {
      currentStatus,
      isApproved: currentStatus.toLowerCase() === "approved",
      isRejected: currentStatus.toLowerCase() === "rejected",
    };
  }, [driverKycStatus?.status, selectedDriver?.status]);

  const driverCode = selectedDriver?.driver_code || selectedDriver?.driverCode || "";

const hasDriverConfig = useMemo(() => {
  if (!driverCode) return false;

  return configRows.some((config) => {
    return (
      String(config.driver_code || config.driverCode || "").trim() ===
      String(driverCode).trim()
    );
  });
}, [configRows, driverCode]);

const hasDriverCharges = useMemo(() => {
  if (!driverCode) return false;

  return chargeRows.some((charge) => {
    const sameDriver =
      String(charge.driverCode || charge.driver_code || "").trim() ===
      String(driverCode).trim();

    const hasPerDay =
      Number(charge.perDayAmount || charge.per_day_amount || 0) > 0;

    const hasPerOrder =
      Number(charge.perOrderAmount || charge.per_order_amount || 0) > 0;

    return sameDriver && hasPerDay && hasPerOrder;
  });
}, [chargeRows, driverCode]);

const canApproveKyc = hasDriverConfig && hasDriverCharges;

const shouldShowApproveButton =
  !statusInfo.isApproved || !canApproveKyc;

  const onSubmitStep = useCallback(
    async (stepNumber, stepData) => {
      await handleStepSubmit(stepNumber, stepData);
    },
    [handleStepSubmit],
  );

  const closeModel = useCallback(() => {
    setOpenKycDialogId(null);
    setSelectedDriver(null);
    setPreviewImage(null);
    setLocalDriverKycStatus(null);
  }, [setOpenKycDialogId, setSelectedDriver, setPreviewImage]);

  const handleImageClick = useCallback(
    (imageSrc) => {
      setPreviewImage(`${imageSrc}?t=${Date.now()}`);
    },
    [setPreviewImage],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewImage(null);
  }, [setPreviewImage]);

  const handleKycStatusUpdate = async (
    kycId,
    status,
    rejectedStep = null,
    comment = "",
  ) => {
    setLoadingAction(status);

    try {
      const payload = {
        adminId: AdminId,
        kycId: kycId.toString(),
        kycStatus: status === "Approve" ? "Approved" : "Rejected",
      };

      //  Only for Reject
      if (status === "Reject") {
        payload.rejectedSteps = [rejectedStep];
        payload.comments = comment;
      }

      const result = await updateDriverKycStatus(payload);
      await fetchDriverKycDetails();
      
      if (result?.responseCode === "00") {
        const updatedStatus = status === "Approve" ? "Approved" : "Rejected";

        // Update selected driver
        setSelectedDriver((prev) => ({
          ...prev,
          status: updatedStatus,
          comments: status === "Reject" ? comment : null,
        }));

        toast.success(result.responseMessage);

        if (activeTab === "Kyc Details") {
          setTimeout(() => {
            fetchDriverKycDetails(false);
          }, 1000);
        }
      } else {
        throw new Error(result?.responseMessage || "Something went wrong");
      }
    } catch (error) {
      console.error("FULL ERROR:", error);
      console.log("SERVER RESPONSE:", error?.response?.data);

      toast.error(
        error?.response?.data?.responseMessage ||
          error.message ||
          `Failed to ${status} KYC`,
      );
    }
  };

  console.log("Selected Driver:", selectedDriver);
  return (
    <div className="p-4 border rounded-md shadow-sm bg-white space-y-4">
      {openKycDialogId && selectedDriver && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeModel}
        >
          <div
            className="bg-white rounded-lg shadow-md max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6 relative mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">KYC Details</h2>
              <button
                onClick={closeModel}
                className="text-gray-600 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-white rounded-2xl shadow-md border border-gray-200 mb-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Driver Details
                  </h2>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Driver Code:
                      </span>{" "}
                      {selectedDriver.driver_code || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Name:</span>{" "}
                      {selectedDriver.name || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Email:</span>{" "}
                      {selectedDriver.email || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Phone:</span>{" "}
                      {selectedDriver.phone || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Aadhaar No:
                      </span>{" "}
                      {selectedDriver.aadhaar_no || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">PAN No:</span>{" "}
                      {selectedDriver.pan_no || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="p-4 bg-white rounded-2xl shadow-md border border-gray-200 mb-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Vehicle Details
                  </h2>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Driving Licence:
                      </span>{" "}
                      {selectedDriver.driving_licence || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Transport Type:
                      </span>{" "}
                      {selectedDriver.transport_type_id || "N/A"} (
                      {selectedDriver.transport_description || "N/A"})
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Licence Plate:
                      </span>{" "}
                      {selectedDriver.licence_plate || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">City:</span>{" "}
                      {selectedDriver.city || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Area:</span>{" "}
                      {selectedDriver.area || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                {images.map((item, index) => (
                  <div key={index} className="relative text-center">
                    {item.src && item.src !== "" && item.src !== "No URL" ? (
                      <div className="relative group">
                        <img
                          src={item.src}
                          alt={item.label}
                          className="w-full h-48 object-cover rounded mb-2 cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => handleImageClick(item.src)}
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://via.placeholder.com/300x200?text=No+Image";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded mb-2 flex flex-col items-center justify-center text-sm text-gray-600">
                        <p className="font-medium">No Image</p>
                      </div>
                    )}
                    <h5 className="font-medium text-gray-800 mt-1">
                      {item.label}
                    </h5>
                  </div>
                ))}
              </div>

              {/* Preview Modal */}
              {previewImage && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                  onClick={handlePreviewClose}
                >
                  <div
                    className="relative bg-white p-2 rounded-lg shadow-xl max-w-4xl max-h-[75vh]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="max-w-full max-h-[65vh] object-contain rounded"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/600x500?text=No+Image";
                      }}
                    />
                    <button
                      onClick={handlePreviewClose}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 hover:bg-gray-100"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              {statusInfo.isRejected && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-left">
                  <p className="text-sm font-semibold text-red-600 mb-1">
                    Rejection Reason:
                  </p>
                  <p className="text-sm text-gray-700">
                    {selectedDriver.comments || "No reason provided"}
                  </p>
                </div>
              )}
             <div className="mt-6 space-y-4">
  {/* STATUS / REQUIREMENT BADGES */}
  <div className="flex flex-wrap items-center justify-center gap-2">
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
        statusInfo.isApproved
          ? "bg-green-100 text-green-700"
          : statusInfo.isRejected
            ? "bg-red-100 text-red-700"
            : "bg-yellow-100 text-yellow-700"
      }`}
    >
      KYC: {statusInfo.currentStatus || "Pending"}
    </span>

    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
        hasDriverConfig
          ? "bg-green-100 text-green-700"
          : "bg-red-100 text-red-700"
      }`}
    >
      Config: {hasDriverConfig ? "Added" : "Missing"}
    </span>

    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
        hasDriverCharges
          ? "bg-green-100 text-green-700"
          : "bg-red-100 text-red-700"
      }`}
    >
      Charges: {hasDriverCharges ? "Added" : "Missing"}
    </span>
  </div>

  {/* ACTION BUTTONS */}
  <div className="flex flex-wrap items-center justify-center gap-3">
    {shouldShowApproveButton && (
      <button
        className={`min-w-[120px] rounded-lg px-6 py-2 text-sm font-semibold text-white transition disabled:opacity-60 disabled:cursor-not-allowed ${
          canApproveKyc && !statusInfo.isApproved
            ? "bg-green-600 hover:bg-green-700"
            : "bg-green-500 hover:bg-green-600"
        }`}
        onClick={() => {
          if (!hasDriverConfig) {
            toast.error("Please add driver config before approving KYC");
            return;
          }

          if (!hasDriverCharges) {
            toast.error("Please add driver charges before approving KYC");
            return;
          }

          if (statusInfo.isApproved) {
            toast.success("KYC is already approved");
            return;
          }

          handleKycStatusUpdate(kycId, "Approve", selectedDriver);
        }}
        disabled={loadingAction === "Approve" || !kycId}
      >
        {loadingAction === "Approve" ? "Processing..." : "Approve"}
      </button>
    )}

    {!statusInfo.isRejected && (
      <button
        className="min-w-[120px] rounded-lg bg-red-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
        onClick={() => setShowRejectBox(true)}
      >
        Reject
      </button>
    )}
  </div>

  {/* HELPER MESSAGE */}
  {shouldShowApproveButton && (!hasDriverConfig || !hasDriverCharges) && (
    <div className="mx-auto max-w-xl rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-center text-xs text-orange-700">
      {!hasDriverConfig && !hasDriverCharges
        ? "Driver config and charges are required before approval."
        : !hasDriverConfig
          ? "Driver config is required before approval."
          : "Driver charges are required before approval."}
    </div>
  )}

  {statusInfo.isApproved && !canApproveKyc && (
    <div className="mx-auto max-w-xl rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-center text-xs text-yellow-700">
      KYC is approved, but config/charges are incomplete.
    </div>
  )}

  {/* REJECT BOX */}
  {showRejectBox && (
    <div className="mx-auto w-full max-w-3xl rounded-xl border border-red-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between border-b border-red-100 pb-3">
        <div>
          <h3 className="text-sm font-semibold text-red-600">Reject KYC</h3>
          <p className="text-xs text-gray-500">
            Select the failed step and add a clear reason.
          </p>
        </div>

        <button
          className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          onClick={() => {
            setShowRejectBox(false);
            setRejectStep("");
            setRejectComment("");
          }}
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Rejection Step
          </label>
          <select
            value={rejectStep}
            onChange={(e) => setRejectStep(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
          >
            <option value="">Select Step</option>
            {images.map((item, index) => (
              <option key={index} value={index + 1}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Reason
          </label>
          <input
            type="text"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Enter rejection reason..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          className="rounded-lg bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
          onClick={() => {
            setShowRejectBox(false);
            setRejectStep("");
            setRejectComment("");
          }}
        >
          Cancel
        </button>

        <button
          className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700"
          onClick={() => {
            if (!rejectStep) {
              toast.error("Please select step");
              return;
            }

            if (!rejectComment) {
              toast.error("Please enter rejection reason");
              return;
            }

            handleKycStatusUpdate(
              kycId,
              "Reject",
              rejectStep,
              rejectComment,
            );

            setShowRejectBox(false);
            setRejectStep("");
            setRejectComment("");
          }}
        >
          Confirm Reject
        </button>
      </div>
    </div>
  )}
</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewKycDetails;
